import {
  ref,
  query,
  orderByChild,
  limitToLast,
  startAt,
  endAt,
  get,
  onChildAdded,
  onChildChanged,
  onChildRemoved,
  onValue,
  DataSnapshot,
  Query,
} from 'firebase/database';

import { NOTIFICATIONS_PAGE_SIZE, REALTIME_LISTENER_LIMIT } from '../../components/notifications/constants';
import { NotificationCursor, NotificationRecord } from '../../redux/types/notifications';
import { db } from './firebase';

// The prefix the preview's own back and notification service write to; baked at build time, empty elsewhere.
const PATH_PREFIX = import.meta.env.VITE_FIREBASE_PATH_PREFIX ? `${import.meta.env.VITE_FIREBASE_PATH_PREFIX}/` : '';

const NOTIFICATIONS_PATH = `${PATH_PREFIX}notifications`;
const STATES_PATH = `${PATH_PREFIX}states`;

export interface NotificationQueryParams {
  uid: string;
  domain: string; // 'all' | 'task' | 'question' | 'alert'
  status: string; // 'all' | 'active' | 'completed' | 'archived'
  pageSize: number;
  cursor?: NotificationCursor;
  dateFromMs?: number;
  dateToMs?: number;
}

/**
 * Builds the filter key from domain and status.
 */
export function buildFilterKey(domain: string, status: string): string {
  return `${domain}_${status}`;
}

/**
 * Parses the filter key into domain and status.
 */
export function parseFilterKey(filterKey: string): { domain: string; status: string } {
  const [domain, status] = filterKey.split('_');
  return { domain: domain || 'all', status: status || 'all' };
}

/**
 * Gets the appropriate index field name based on the filter combination.
 */
function getIndexField(domain: string, status: string): string {
  if (domain === 'all' && status === 'all') {
    return 'createdAt_index';
  }
  if (domain !== 'all' && status === 'all') {
    return 'sourceType_createdAt_index';
  }
  if (domain === 'all' && status !== 'all') {
    return 'status_createdAt_index';
  }
  return 'sourceType_status_createdAt_index';
}

/**
 * Gets the prefix for range queries based on the filter.
 */
function getQueryPrefix(domain: string, status: string): string {
  if (domain === 'all' && status === 'all') {
    return '';
  }
  if (domain !== 'all' && status === 'all') {
    return `${domain}_`;
  }
  if (domain === 'all' && status !== 'all') {
    return `${status}_`;
  }
  return `${domain}_${status}_`;
}

/**
 * Gets the cursor value from a notification based on the current filter.
 */
export function getCursorFromNotification(
  key: string,
  notification: NotificationRecord,
  domain: string,
  status: string
): NotificationCursor {
  let value: number | string;

  if (domain === 'all' && status === 'all') {
    value = notification.createdAt_index;
  } else if (domain !== 'all' && status === 'all') {
    value = notification.sourceType_createdAt_index;
  } else if (domain === 'all' && status !== 'all') {
    value = notification.status_createdAt_index;
  } else {
    value = notification.sourceType_status_createdAt_index;
  }

  return { key, value };
}

/**
 * Extracts the numeric timestamp from the end of a composite cursor string.
 * Composite cursors have the format "{prefix}_{timestamp}" (e.g. "task_active_1700000000000").
 * Returns null if the trailing segment is not a finite number.
 */
function parseCursorTimestamp(cursor: NotificationCursor): number | null {
  const rawTimestamp = String(cursor.value).split('_').pop();
  if (!rawTimestamp) {
    return null;
  }

  const parsed = Number(rawTimestamp);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Builds a Firebase query for notifications with the given filter parameters.
 */
function buildNotificationQuery(params: NotificationQueryParams): Query {
  const { uid, domain, status, pageSize, cursor, dateFromMs, dateToMs } = params;
  const notificationsRef = ref(db, `${NOTIFICATIONS_PATH}/${uid}`);
  const indexField = getIndexField(domain, status);
  const prefix = getQueryPrefix(domain, status);

  const queryConstraints: Parameters<typeof query>[1][] = [orderByChild(indexField)];

  if (domain === 'all' && status === 'all') {
    const lowerBound = dateFromMs;
    const cursorTimestamp = cursor ? Number(cursor.value) : null;
    const upperBound =
      cursorTimestamp != null && dateToMs != null ? Math.min(cursorTimestamp, dateToMs) : cursorTimestamp ?? dateToMs;

    if (lowerBound != null) {
      queryConstraints.push(startAt(lowerBound));
    }

    if (upperBound != null) {
      queryConstraints.push(
        cursor && upperBound === cursorTimestamp ? endAt(upperBound, cursor.key) : endAt(upperBound)
      );
    }

    if (cursor) {
      queryConstraints.push(limitToLast(pageSize + 1));
    } else {
      queryConstraints.push(limitToLast(pageSize));
    }
  } else if (cursor) {
    const rangeStart = dateFromMs != null ? `${prefix}${dateFromMs}` : prefix;
    const cursorTimestamp = parseCursorTimestamp(cursor);
    const upperBoundTimestamp =
      cursorTimestamp != null && dateToMs != null ? Math.min(cursorTimestamp, dateToMs) : cursorTimestamp ?? dateToMs;
    const rangeEnd = upperBoundTimestamp != null ? `${prefix}${upperBoundTimestamp}` : `${prefix}\uf8ff`;

    queryConstraints.push(startAt(rangeStart));
    queryConstraints.push(upperBoundTimestamp === cursorTimestamp ? endAt(rangeEnd, cursor.key) : endAt(rangeEnd));
    queryConstraints.push(limitToLast(pageSize + 1));
  } else {
    const rangeStart = dateFromMs != null ? `${prefix}${dateFromMs}` : prefix;
    const rangeEnd = dateToMs != null ? `${prefix}${dateToMs}` : `${prefix}\uf8ff`;

    queryConstraints.push(startAt(rangeStart));
    queryConstraints.push(endAt(rangeEnd));
    queryConstraints.push(limitToLast(pageSize));
  }

  return query(notificationsRef, ...queryConstraints);
}

function notificationMatchesListener(
  notification: NotificationRecord,
  domain: string,
  status: string,
  dateFromMs: number | undefined,
  dateToMs: number | undefined
): boolean {
  if (domain !== 'all' && notification.sourceType !== domain) return false;
  if (status !== 'all' && notification.status !== status) return false;
  if (dateFromMs != null && notification.createdAt_index < dateFromMs) return false;
  if (dateToMs != null && notification.createdAt_index > dateToMs) return false;
  return true;
}

/**
 * Loads notifications with the given filter parameters.
 * Returns notifications, cursor for next page, and whether there are more items.
 */
export async function loadNotificationsWithFilter(
  params: NotificationQueryParams
): Promise<{ notifications: Record<string, NotificationRecord>; cursor: NotificationCursor | null; hasMore: boolean }> {
  const { domain, status, pageSize, cursor } = params;
  const notificationsQuery = buildNotificationQuery(params);

  const snapshot = await get(notificationsQuery);
  const data = snapshot.val() as Record<string, NotificationRecord> | null;

  if (!data) {
    return { notifications: {}, cursor: null, hasMore: false };
  }

  // Convert to array and sort by createdAt_index descending
  const entries = Object.entries(data).sort(
    ([keyA, a], [keyB, b]) => b.createdAt_index - a.createdAt_index || keyB.localeCompare(keyA)
  );

  // If we had a cursor, we need to exclude the cursor item (it was included for pagination)
  let filteredEntries = entries;
  if (cursor) {
    filteredEntries = entries.filter(([key]) => key !== cursor.key);
  }
  filteredEntries = filteredEntries.slice(0, pageSize);

  // Convert back to record
  const notifications: Record<string, NotificationRecord> = {};
  filteredEntries.forEach(([key, notification]) => {
    notifications[key] = notification;
  });

  // Determine if there are more items
  const hasMore = cursor ? entries.length > pageSize : entries.length === pageSize;

  // Get the oldest item's cursor for the next page
  let newCursor: NotificationCursor | null = null;
  if (filteredEntries.length > 0) {
    const oldestEntry = filteredEntries[filteredEntries.length - 1];
    newCursor = getCursorFromNotification(oldestEntry[0], oldestEntry[1], domain, status);
  }

  return { notifications, cursor: newCursor, hasMore };
}

/**
 * Loads initial notifications and user state for a filter.
 */
export async function loadInitialNotificationsWithFilter(
  uid: string,
  domain: string,
  status: string
): Promise<{
  notifications: Record<string, NotificationRecord>;
  cursor: NotificationCursor | null;
  hasMore: boolean;
  unseenCount: number;
  readAllBefore: number | null;
}> {
  const unseenCountPromise = get(ref(db, `${STATES_PATH}/${uid}/unseen/count`));
  const readAllBeforePromise = get(ref(db, `${STATES_PATH}/${uid}/readAllBefore`));
  const notificationsPromise = loadNotificationsWithFilter({
    uid,
    domain,
    status,
    pageSize: NOTIFICATIONS_PAGE_SIZE,
  }).catch((error) => {
    // eslint-disable-next-line no-console
    console.error('Error loading initial notifications:', error);
    return { notifications: {}, cursor: null, hasMore: false };
  });
  const [unseenCountSnapshot, readAllBeforeSnapshot, result] = await Promise.all([
    unseenCountPromise,
    readAllBeforePromise,
    notificationsPromise,
  ]);
  const storedUnseenCount = unseenCountSnapshot.val() as unknown;
  const storedReadAllBefore = readAllBeforeSnapshot.val() as unknown;
  const unseenCount = typeof storedUnseenCount === 'number' ? Math.max(0, storedUnseenCount) : 0;
  const readAllBefore =
    typeof storedReadAllBefore === 'number' && Number.isSafeInteger(storedReadAllBefore) ? storedReadAllBefore : null;

  return {
    ...result,
    unseenCount,
    readAllBefore,
  };
}

// Filtered listener state management
interface FilteredListenerState {
  unsubscribe: () => void;
  filterKey: string;
}

let currentFilteredListener: FilteredListenerState | null = null;

/**
 * Starts a filtered notifications listener for real-time updates.
 * Automatically stops any previous listener before starting a new one.
 * Only one listener can be active at a time; calling this replaces any existing listener.
 *
 * Strategy: uses onValue (onlyOnce: true) on the same query object for the initial load,
 * then onChildAdded/Changed/Removed on that same object for real-time changes.
 * Because the same Query reference is reused, the Firebase SDK shares the WebSocket connection
 * and local cache — no duplicate network round-trips.
 *
 * onChildAdded events emitted during the initial sync are ignored, and the initial data is
 * delivered only via onInitialLoad from onValue (onlyOnce).
 *
 * @param dateFromMs - Optional lower bound (ms). Only notifications created on or after this date are included.
 * @param dateToMs - Optional upper bound (ms). Only notifications created on or before this date are included.
 * @returns Unsubscribe function to stop the listener.
 */
export function startFilteredNotificationsListener(
  uid: string,
  domain: string,
  status: string,
  callbacks: {
    onInitialLoad: (notifications: Record<string, NotificationRecord>) => void;
    onChildAdded: (key: string, notification: NotificationRecord) => void;
    onChildChanged: (key: string, notification: NotificationRecord) => void;
    onChildRemoved: (key: string) => void;
  },
  dateFromMs?: number,
  dateToMs?: number
): () => void {
  const filterKey = buildFilterKey(domain, status);

  // Stop previous listener if it exists
  if (currentFilteredListener) {
    currentFilteredListener.unsubscribe();
  }

  const notificationsRef = ref(db, `${NOTIFICATIONS_PATH}/${uid}`);
  const indexField = getIndexField(domain, status);
  const prefix = getQueryPrefix(domain, status);

  let listenerQuery: Query;

  if (domain === 'all' && status === 'all') {
    const constraints: Parameters<typeof query>[1][] = [orderByChild(indexField)];

    if (dateFromMs != null) {
      constraints.push(startAt(dateFromMs));
    }

    if (dateToMs != null) {
      constraints.push(endAt(dateToMs));
    }

    constraints.push(limitToLast(REALTIME_LISTENER_LIMIT));
    listenerQuery = query(notificationsRef, ...constraints);
  } else {
    const rangeStart = dateFromMs != null ? `${prefix}${dateFromMs}` : prefix;
    const rangeEnd = dateToMs != null ? `${prefix}${dateToMs}` : `${prefix}\uf8ff`;

    listenerQuery = query(
      notificationsRef,
      orderByChild(indexField),
      startAt(rangeStart),
      endAt(rangeEnd),
      limitToLast(REALTIME_LISTENER_LIMIT)
    );
  }

  let initialSyncComplete = false;
  let closed = false;

  const handleChildAdded = (snapshot: DataSnapshot): void => {
    if (closed) return;

    const { key } = snapshot;
    if (!key) return;

    if (!initialSyncComplete) {
      // Existing children replayed during initial sync are handled by onInitialLoad.
      return;
    }

    const notification = snapshot.val() as NotificationRecord;
    if (notification) {
      callbacks.onChildAdded(key, notification);
    }
  };

  const handleChildChanged = (snapshot: DataSnapshot): void => {
    if (closed) return;

    const notification = snapshot.val() as NotificationRecord;
    const { key } = snapshot;
    if (notification && key) {
      callbacks.onChildChanged(key, notification);
    }
  };

  const handleChildRemoved = (snapshot: DataSnapshot): void => {
    const { key } = snapshot;
    if (!key || closed) return;

    // A limited RTDB query also emits child_removed when a record merely falls out of
    // the top window. Check the canonical record before deleting it from paginated cache.
    void get(ref(db, `${NOTIFICATIONS_PATH}/${uid}/${key}`))
      .then((currentSnapshot) => {
        if (closed) return;

        const currentNotification = currentSnapshot.val() as NotificationRecord | null;
        if (
          currentNotification &&
          notificationMatchesListener(currentNotification, domain, status, dateFromMs, dateToMs)
        ) {
          return;
        }

        callbacks.onChildRemoved(key);
      })
      .catch(() => undefined);
  };

  const unsubscribeAdded = onChildAdded(listenerQuery, handleChildAdded);
  const unsubscribeChanged = onChildChanged(listenerQuery, handleChildChanged);
  const unsubscribeRemoved = onChildRemoved(listenerQuery, handleChildRemoved);

  const unsubscribeInitial = onValue(
    listenerQuery,
    (snapshot: DataSnapshot) => {
      if (closed) return;

      const data = snapshot.val() as Record<string, NotificationRecord> | null;
      const notifications: Record<string, NotificationRecord> = data ?? {};
      initialSyncComplete = true;
      callbacks.onInitialLoad(notifications);
    },
    { onlyOnce: true }
  );

  const unsubscribe = (): void => {
    closed = true;
    unsubscribeInitial();
    unsubscribeAdded();
    unsubscribeChanged();
    unsubscribeRemoved();

    if (currentFilteredListener?.unsubscribe === unsubscribe) {
      currentFilteredListener = null;
    }
  };

  currentFilteredListener = { unsubscribe, filterKey };

  return unsubscribe;
}

/**
 * Stops the current filtered notifications listener.
 */
export function stopFilteredNotificationsListener(): void {
  if (currentFilteredListener) {
    currentFilteredListener.unsubscribe();
    currentFilteredListener = null;
  }
}

/**
 * Gets the current filter key being listened to.
 */
export function getCurrentListenerFilterKey(): string | null {
  return currentFilteredListener?.filterKey ?? null;
}

// Unseen count listener state management
type UnseenCountCallback = (count: number) => void;

let unseenCountUnsubscribe: (() => void) | null = null;
let readAllBeforeUnsubscribe: (() => void) | null = null;

/**
 * Starts listening to unseenCount changes in real-time.
 * Listens to states/{uid}/unseen/count and calls the callback whenever it changes.
 * Automatically stops any previous listener before starting a new one.
 */
export function startUnseenCountListener(uid: string, onUnseenCountChange: UnseenCountCallback): void {
  if (unseenCountUnsubscribe) {
    unseenCountUnsubscribe();
  }

  const statesRef = ref(db, `${STATES_PATH}/${uid}/unseen/count`);
  unseenCountUnsubscribe = onValue(
    statesRef,
    (snapshot) => {
      const count = snapshot.val() as number | null;
      onUnseenCountChange(Math.max(0, count ?? 0));
    },
    (error) => {
      // eslint-disable-next-line no-console
      console.error('[Firebase] unseenCount listener error:', error);
    }
  );
}

/**
 * Stops the current unseenCount listener.
 */
export function stopUnseenCountListener(): void {
  if (unseenCountUnsubscribe) {
    unseenCountUnsubscribe();
    unseenCountUnsubscribe = null;
  }
}

export function startReadAllBeforeListener(uid: string, onReadAllBeforeChange: (value: number) => void): void {
  if (readAllBeforeUnsubscribe) {
    readAllBeforeUnsubscribe();
  }

  const readAllBeforeRef = ref(db, `${STATES_PATH}/${uid}/readAllBefore`);
  readAllBeforeUnsubscribe = onValue(
    readAllBeforeRef,
    (snapshot) => {
      const value = snapshot.val() as unknown;
      if (typeof value === 'number' && Number.isSafeInteger(value)) {
        onReadAllBeforeChange(value);
      }
    },
    (error) => {
      // eslint-disable-next-line no-console
      console.error('[Firebase] readAllBefore listener error:', error);
    }
  );
}

export function stopReadAllBeforeListener(): void {
  if (readAllBeforeUnsubscribe) {
    readAllBeforeUnsubscribe();
    readAllBeforeUnsubscribe = null;
  }
}
